import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, addDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

interface VenueRequest {
  id: string;
  userId: string;
  venueName: string;
  location: string;
  phone: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: any;
  userName?: string;
  userEmail?: string;
}

interface VenueOperator {
  id: string;
  displayName: string;
  email: string;
  venueId?: string;
  venues?: Venue[];
}

interface Venue {
  id: string;
  name: string;
  address: string;
  operatorId: string;
  boards?: Board[];
}

interface Board {
  id: string;
  boardNumber: number;
  name: string;
  venueId: string;
  isActive: boolean;
}

const VenueRequests: React.FC = () => {
  const { isAdmin } = useAuth();
  const [requests, setRequests] = useState<VenueRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [confirmReject, setConfirmReject] = useState<string | null>(null);
  const [operators, setOperators] = useState<VenueOperator[]>([]);
  const [expandedOperators, setExpandedOperators] = useState<Set<string>>(new Set());
  const [expandedVenues, setExpandedVenues] = useState<Set<string>>(new Set());
  const [loadingOperators, setLoadingOperators] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      loadRequests();
      loadOperators();
    }
  }, [isAdmin]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, 'venueRequests'),
        where('status', '==', 'pending')
      );
      const snapshot = await getDocs(q);

      const requestsData: VenueRequest[] = [];

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();

        // Fetch user details
        let userName = 'Ismeretlen';
        let userEmail = '';
        try {
          const userDoc = await getDocs(query(collection(db, 'users'), where('__name__', '==', data.userId)));
          if (!userDoc.empty) {
            const userData = userDoc.docs[0].data();
            userName = userData.displayName || userData.username || 'Ismeretlen';
            userEmail = userData.email || '';
          }
        } catch (err) {
          console.error('Error fetching user:', err);
        }

        requestsData.push({
          id: docSnap.id,
          userId: data.userId,
          venueName: data.venueName,
          location: data.location,
          phone: data.phone,
          status: data.status,
          createdAt: data.createdAt,
          userName,
          userEmail
        });
      }

      setRequests(requestsData);
    } catch (error) {
      console.error('Error loading venue requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const approveRequest = async (request: VenueRequest) => {
    setProcessing(request.id);
    try {
      // 1. Create venue
      const venueData = {
        name: request.venueName,
        address: request.location,
        city: extractCity(request.location),
        country: 'Hungary',
        phone: request.phone,
        operatorId: request.userId,
        isActive: true,
        status: 'active',
        pricePerHour: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        openingHours: {
          monday: { open: '09:00', close: '22:00', closed: false },
          tuesday: { open: '09:00', close: '22:00', closed: false },
          wednesday: { open: '09:00', close: '22:00', closed: false },
          thursday: { open: '09:00', close: '22:00', closed: false },
          friday: { open: '09:00', close: '23:00', closed: false },
          saturday: { open: '10:00', close: '23:00', closed: false },
          sunday: { open: '10:00', close: '20:00', closed: false }
        }
      };

      const venueRef = await addDoc(collection(db, 'venues'), venueData);
      console.log('Venue created:', venueRef.id);

      // 2. Grant venue operator role to user
      const userRef = doc(db, 'users', request.userId);
      await updateDoc(userRef, {
        isVenueOperator: true,
        venueId: venueRef.id,
        updatedAt: serverTimestamp()
      });
      console.log('User granted operator role');

      // 3. Update request status
      const requestRef = doc(db, 'venueRequests', request.id);
      await updateDoc(requestRef, {
        status: 'approved',
        approvedAt: serverTimestamp(),
        venueId: venueRef.id,
        updatedAt: serverTimestamp()
      });

      // Reload requests
      await loadRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      alert('Hiba történt a kérelem jóváhagyásakor. Kérjük próbálja újra.');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectClick = (requestId: string) => {
    setConfirmReject(requestId);
  };

  const confirmRejectRequest = async () => {
    if (!confirmReject) return;

    setProcessing(confirmReject);
    try {
      const requestRef = doc(db, 'venueRequests', confirmReject);
      await updateDoc(requestRef, {
        status: 'rejected',
        rejectedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      await loadRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('Hiba történt a kérelem elutasításakor.');
    } finally {
      setProcessing(null);
      setConfirmReject(null);
    }
  };

  const cancelReject = () => {
    setConfirmReject(null);
  };

  const loadOperators = async () => {
    setLoadingOperators(true);
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const operatorsData: VenueOperator[] = [];

      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        if (userData.isVenueOperator) {
          operatorsData.push({
            id: userDoc.id,
            displayName: userData.displayName || userData.username || userData.email || 'Ismeretlen',
            email: userData.email || '',
            venueId: userData.venueId
          });
        }
      }

      setOperators(operatorsData);
    } catch (error) {
      console.error('Error loading operators:', error);
    } finally {
      setLoadingOperators(false);
    }
  };

  const loadOperatorVenues = async (operatorId: string) => {
    try {
      const venuesSnapshot = await getDocs(collection(db, 'venues'));
      const venues: Venue[] = [];

      venuesSnapshot.forEach((venueDoc) => {
        const venueData = venueDoc.data();
        if (venueData.operatorId === operatorId) {
          venues.push({
            id: venueDoc.id,
            name: venueData.name || 'Névtelen helyszín',
            address: venueData.address || '',
            operatorId: venueData.operatorId
          });
        }
      });

      setOperators(prev => prev.map(op =>
        op.id === operatorId ? { ...op, venues } : op
      ));
    } catch (error) {
      console.error('Error loading venues:', error);
    }
  };

  const loadVenueBoards = async (venueId: string, operatorId: string) => {
    try {
      const boardsSnapshot = await getDocs(collection(db, 'boards'));
      const boards: Board[] = [];

      boardsSnapshot.forEach((boardDoc) => {
        const boardData = boardDoc.data();
        if (boardData.venueId === venueId) {
          boards.push({
            id: boardDoc.id,
            boardNumber: boardData.boardNumber || 0,
            name: boardData.name || `Tábla ${boardData.boardNumber}`,
            venueId: boardData.venueId,
            isActive: boardData.isActive || false
          });
        }
      });

      setOperators(prev => prev.map(op => {
        if (op.id === operatorId && op.venues) {
          return {
            ...op,
            venues: op.venues.map(v =>
              v.id === venueId ? { ...v, boards } : v
            )
          };
        }
        return op;
      }));
    } catch (error) {
      console.error('Error loading boards:', error);
    }
  };

  const toggleOperator = async (operatorId: string) => {
    const newExpanded = new Set(expandedOperators);
    if (newExpanded.has(operatorId)) {
      newExpanded.delete(operatorId);
    } else {
      newExpanded.add(operatorId);
      const operator = operators.find(op => op.id === operatorId);
      if (operator && !operator.venues) {
        await loadOperatorVenues(operatorId);
      }
    }
    setExpandedOperators(newExpanded);
  };

  const toggleVenue = async (venueId: string, operatorId: string) => {
    const newExpanded = new Set(expandedVenues);
    if (newExpanded.has(venueId)) {
      newExpanded.delete(venueId);
    } else {
      newExpanded.add(venueId);
      const operator = operators.find(op => op.id === operatorId);
      const venue = operator?.venues?.find(v => v.id === venueId);
      if (venue && !venue.boards) {
        await loadVenueBoards(venueId, operatorId);
      }
    }
    setExpandedVenues(newExpanded);
  };

  const extractCity = (location: string): string => {
    // Simple city extraction - assumes format like "Budapest, Váci utca 1."
    const parts = location.split(',');
    return parts[0].trim();
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return '';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('hu-HU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      return '';
    }
  };

  if (!isAdmin) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="card-dark p-8 text-center">
          <p className="text-[#B8B8C3]">Nincs jogosultsága az oldal megtekintéséhez.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-8">
      {/* Pending Requests Section */}
      <div>
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">🏢 Üzemeltetői kérelmek</h1>
          <p className="text-[#B8B8C3]">
            Felhasználók által beküldött üzemeltetői kérelmek kezelése
          </p>
        </div>

        {requests.length === 0 ? (
          <div className="card-dark p-12 text-center">
            <div className="w-20 h-20 bg-[#15191E] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-[#B8B8C3]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Nincsenek függőben lévő kérelmek</h3>
            <p className="text-[#B8B8C3]">Jelenleg nincs új üzemeltetői kérelem.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => (
              <div key={request.id} className="card-dark p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-1">{request.venueName}</h3>
                    <p className="text-[#B8B8C3] text-sm mb-3">{formatDate(request.createdAt)}</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-[#8a8f99] mb-1">Kérelmező</p>
                        <p className="text-white font-semibold">{request.userName}</p>
                        {request.userEmail && (
                          <p className="text-[#B8B8C3] text-sm">{request.userEmail}</p>
                        )}
                      </div>

                      <div>
                        <p className="text-sm text-[#8a8f99] mb-1">Helyszín</p>
                        <p className="text-white">{request.location}</p>
                      </div>

                      <div>
                        <p className="text-sm text-[#8a8f99] mb-1">Kapcsolat</p>
                        <p className="text-white text-sm">{request.phone}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4 border-t border-[#31383D]">
                  <button
                    onClick={() => approveRequest(request)}
                    disabled={processing === request.id}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-lg hover:shadow-[0_0_20px_rgba(34,197,94,0.5)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processing === request.id ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Feldolgozás...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Jóváhagyás
                      </span>
                    )}
                  </button>

                  <button
                    onClick={() => handleRejectClick(request.id)}
                    disabled={processing === request.id}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-lg hover:shadow-[0_0_20px_rgba(239,68,68,0.5)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Elutasítás
                    </span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Confirmation Modal */}
        {confirmReject && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-[#15191E] rounded-lg p-6 max-w-md w-full mx-4 border border-[#31383D]">
              <h3 className="text-xl font-bold text-white mb-4">Kérelem elutasítása</h3>
              <p className="text-[#B8B8C3] mb-6">
                Biztosan elutasítja ezt a kérelmet? Ez a művelet nem vonható vissza.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={cancelReject}
                  className="flex-1 px-4 py-2 bg-[#31383D] text-white rounded-lg hover:bg-[#3d4758] transition-colors"
                >
                  Mégse
                </button>
                <button
                  onClick={confirmRejectRequest}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:shadow-[0_0_20px_rgba(239,68,68,0.5)] transition-all"
                >
                  Elutasítás
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Venue Operators Section */}
      <div>
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-white mb-2">👥 Üzemeltetők és helyszíneik</h2>
          <p className="text-[#B8B8C3]">
            Összes üzemeltető jogosultságú felhasználó helyszíneikkel és tábláikkal
          </p>
        </div>

        {loadingOperators ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
          </div>
        ) : operators.length === 0 ? (
          <div className="card-dark p-12 text-center">
            <div className="w-20 h-20 bg-[#15191E] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-[#B8B8C3]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Nincsenek üzemeltetők</h3>
            <p className="text-[#B8B8C3]">Jelenleg nincs üzemeltető jogosultságú felhasználó.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {operators.map((operator) => (
              <div key={operator.id} className="card-dark">
                {/* Operator Header */}
                <div
                  onClick={() => toggleOperator(operator.id)}
                  className="p-4 cursor-pointer hover:bg-[#1d232a] transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                      {operator.displayName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">{operator.displayName}</h3>
                      <p className="text-sm text-[#B8B8C3]">{operator.email}</p>
                    </div>
                  </div>
                  <svg
                    className={`w-6 h-6 text-[#B8B8C3] transition-transform ${expandedOperators.has(operator.id) ? 'rotate-180' : ''
                      }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>

                {/* Operator's Venues */}
                {expandedOperators.has(operator.id) && (
                  <div className="border-t border-[#31383D] bg-[#15191E] p-4">
                    {!operator.venues ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
                      </div>
                    ) : operator.venues.length === 0 ? (
                      <p className="text-[#B8B8C3] text-center py-4">Nincs helyszín hozzárendelve</p>
                    ) : (
                      <div className="space-y-2">
                        {operator.venues.map((venue) => (
                          <div key={venue.id} className="bg-[#1d232a] rounded-lg overflow-hidden">
                            {/* Venue Header */}
                            <div
                              onClick={() => toggleVenue(venue.id, operator.id)}
                              className="p-3 cursor-pointer hover:bg-[#31383D] transition-colors flex items-center justify-between"
                            >
                              <div className="flex items-center gap-3">
                                <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                <div>
                                  <p className="font-semibold text-white">{venue.name}</p>
                                  <p className="text-xs text-[#B8B8C3]">{venue.address}</p>
                                </div>
                              </div>
                              <svg
                                className={`w-5 h-5 text-[#B8B8C3] transition-transform ${expandedVenues.has(venue.id) ? 'rotate-180' : ''
                                  }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>

                            {/* Venue's Boards */}
                            {expandedVenues.has(venue.id) && (
                              <div className="border-t border-[#15191E] bg-[#15191E] p-3">
                                {!venue.boards ? (
                                  <div className="flex items-center justify-center py-3">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-400"></div>
                                  </div>
                                ) : venue.boards.length === 0 ? (
                                  <p className="text-[#8a8f99] text-sm text-center py-3">Nincs tábla</p>
                                ) : (
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {venue.boards.map((board) => (
                                      <div
                                        key={board.id}
                                        className="bg-[#1d232a] p-3 rounded flex items-center justify-between"
                                      >
                                        <div className="flex items-center gap-2">
                                          <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                          </svg>
                                          <div>
                                            <p className="text-white text-sm font-semibold">{board.name}</p>
                                            <p className="text-xs text-[#B8B8C3]">#{board.boardNumber}</p>
                                          </div>
                                        </div>
                                        <span
                                          className={`px-2 py-1 text-xs rounded ${board.isActive
                                            ? 'bg-green-900/20 text-green-400 border border-green-500'
                                            : 'bg-red-900/20 text-red-400 border border-red-500'
                                            }`}
                                        >
                                          {board.isActive ? 'Aktív' : 'Inaktív'}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VenueRequests;
